// @ts-check
// Casos de retenção (/service/retention; contrato 10 de atendimento, seção 24.5): abas "Casos abertos" (família,
// pontuação, dono, próximo contato e desfecho, com o caso aberto ali mesmo), "Famílias em risco" (risco alto ou médio
// sem caso, com "Abrir caso") e "Sinais recentes". A aba fica na URL (?tab=).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Link, searchParamsState, updateSearchParams } from '../../app/router.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { fetchRetentionOverview } from '../../data/retention.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { OUTCOME_COLORS, RETENTION_TABS, nextContactState, readRetentionTab } from '../../lib/retention-display.js';
import { formatDate, formatDateTime } from '../../lib/format.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { TabList } from '../../ui/tab-list.js';
import { t } from '../../i18n/index.js';
import { RetentionRiskTag, RetentionSignalSource } from './household-retention-section.js';
import { RetentionCaseSection } from './retention-case-section.js';
import { RetentionDialogHost, openRetentionDialog } from './retention-dialogs.js';

/** @typedef {import('../../data/retention.js').RetentionOverview} RetentionOverview */

const TAB_ICONS = Object.freeze({ cases: 'IconHeartHandshake', households: 'IconAlertTriangle', signals: 'IconTrendingDown' });

/** @param {string | null} id */
function memberName(id) {
  const member = (workspaceMembersState.value ?? []).find((candidate) => candidate.id === id);
  return member ? [member.name?.firstName, member.name?.lastName].filter(Boolean).join(' ') : null;
}

/** @param {{ nextContactAt: string | null }} props */
function NextContact({ nextContactAt }) {
  const state = nextContactState(nextContactAt);
  if (state === 'NONE') return html`<span class="retention-muted">${t('retention.nextContact.NONE')}</span>`;
  return html`<span class="retention-next-contact" data-state=${state}>${t(`retention.nextContact.${state}`, { date: formatDateTime(/** @type {string} */ (nextContactAt)) })}</span>`;
}

/** @param {{ overview: RetentionOverview, onChanged: () => void }} props */
function CasesTab({ overview, onChanged }) {
  const [expandedId, setExpandedId] = useState(/** @type {string | null} */ (null));
  if (overview.cases.length === 0) {
    return html`<div class="retention-empty"><${EmptyState} icon="IconHeartHandshake" title=${t('retention.page.emptyCases')} subtitle=${t('retention.page.emptyCasesHint')} /></div>`;
  }
  return html`<ul class="retention-list" data-testid="retention-cases">
    ${overview.cases.map((entry) => {
      const isExpanded = expandedId === entry.ticketId;
      return html`<li key=${entry.ticketId} class="retention-card" data-expanded=${isExpanded ? 'true' : 'false'}>
        <div class="retention-card-row">
          <button type="button" class="retention-card-toggle" aria-expanded=${isExpanded ? 'true' : 'false'} onClick=${() => setExpandedId(isExpanded ? null : entry.ticketId)}>
            <${Icon} name=${isExpanded ? 'IconChevronDown' : 'IconChevronRight'} size=${16} />
            <span class="retention-card-family">${entry.householdName ?? t('retention.page.family')}</span>
          </button>
          <${RetentionRiskTag} risk=${entry.risk} score=${entry.score} />
        </div>
        <div class="retention-card-meta">
          ${entry.protocol ? html`<span><${Icon} name="IconHash" size=${14} />${entry.protocol}</span>` : null}
          <span><${Icon} name="IconUserCircle" size=${14} />${memberName(entry.assigneeId) ?? t('retention.page.noOwner')}</span>
          <${NextContact} nextContactAt=${entry.nextContactAt} />
          <${Tag} color=${OUTCOME_COLORS[entry.outcome ?? ''] ?? 'gray'} text=${t(`retention.outcomes.${entry.outcome ?? 'NONE'}`)} />
          <span class="retention-muted">${t(`retention.openedBy.${entry.openedBy}`)} · ${formatDate(entry.openedAt)}</span>
        </div>
        <div class="retention-card-links">
          <${Link} href=${`/object/household/${entry.householdId}`}>${t('retention.page.openFamily')}<//>
          <${Link} href=${`/object/serviceTicket/${entry.ticketId}`}>${t('retention.page.openTicket')}<//>
        </div>
        ${isExpanded ? html`<${RetentionCaseSection} ticketId=${entry.ticketId} onChanged=${onChanged} />` : null}
      </li>`;
    })}
  </ul>`;
}

/** @param {{ overview: RetentionOverview, onChanged: () => void }} props */
function HouseholdsTab({ overview, onChanged }) {
  if (overview.riskyHouseholds.length === 0) {
    return html`<div class="retention-empty"><${EmptyState} icon="IconMoodSmile" title=${t('retention.page.emptyHouseholds')} /></div>`;
  }
  const canOpen = hasCurrentPermissionFlag('SERVICE_DESK');
  return html`<ul class="retention-list" data-testid="retention-households">
    ${overview.riskyHouseholds.map((household) => html`<li key=${household.householdId} class="retention-card">
      <div class="retention-card-row">
        <${Link} href=${`/object/household/${household.householdId}`} className="retention-card-family">${household.householdName ?? t('retention.page.family')}<//>
        <${RetentionRiskTag} risk=${household.risk} score=${household.score} />
      </div>
      <div class="retention-card-meta">
        ${household.signalKeys.map((key) => html`<span key=${key} class="retention-chip">${t(`retention.signals.${key}`)}</span>`)}
        ${household.lastSignalAt ? html`<span class="retention-muted">${formatDate(household.lastSignalAt)}</span>` : null}
      </div>
      ${canOpen
        ? html`<div class="retention-card-actions">
            <${Button} title=${t('retention.page.openCase')} icon="IconHeartHandshake" variant="secondary" size="small"
              onClick=${() => openRetentionDialog({ kind: 'open', householdId: household.householdId, householdName: household.householdName ?? '', onDone: onChanged })} />
          </div>`
        : null}
    </li>`)}
  </ul>`;
}

/** @param {{ overview: RetentionOverview }} props */
function SignalsTab({ overview }) {
  if (overview.recentSignals.length === 0) {
    return html`<div class="retention-empty"><${EmptyState} icon="IconTrendingDown" title=${t('retention.page.emptySignals')} /></div>`;
  }
  return html`<ul class="retention-list" data-testid="retention-signals">
    ${overview.recentSignals.map((signal) => html`<li key=${signal.id} class="retention-card retention-signal-card" data-active=${signal.isActive ? 'true' : 'false'}>
      <div class="retention-card-row">
        <span class="retention-signal-name">${t(`retention.signals.${signal.signalKey}`)}</span>
        <span class="retention-signal-weight">${signal.isActive ? `+${signal.weight}` : t(signal.isDismissed ? 'retention.page.dismissed' : 'retention.page.expired')}</span>
      </div>
      <div class="retention-card-meta">
        <${Link} href=${`/object/household/${signal.householdId}`}>${signal.householdName ?? t('retention.page.family')}<//>
        <${RetentionSignalSource} signal=${signal} />
        <span class="retention-muted">${formatDate(signal.occurredAt)}</span>
      </div>
    </li>`)}
  </ul>`;
}

export function RetentionPage() {
  const tab = readRetentionTab(searchParamsState.value.get('tab'));
  const [overview, setOverview] = useState(/** @type {RetentionOverview | null | 'error'} */ (null));
  const load = () => {
    fetchRetentionOverview().then(setOverview).catch((error) => {
      console.error(error);
      setOverview('error');
    });
  };
  useEffect(() => {
    load();
    loadWorkspaceMembers();
  }, []);
  const canConfigure = hasCurrentPermissionFlag('SERVICE_DESK_SETTINGS');
  const ready = overview && overview !== 'error' ? overview : null;
  const tabs = RETENTION_TABS.map((id) => ({
    id, title: t(`retention.tabs.${id}`), icon: TAB_ICONS[id],
    pill: ready ? { cases: ready.cases.length, households: ready.riskyHouseholds.length, signals: null }[id] : null,
  }));
  const actions = html`<div class="retention-header-actions">
    <${LightIconButton} icon="IconRefresh" ariaLabel=${t('retention.page.refresh')} onClick=${load} />
    ${canConfigure ? html`<${Link} href="/settings/service-desk?tab=retention" className="retention-settings-link"><${Icon} name="IconSettings" size=${14} />${t('retention.page.settings')}<//>` : null}
  </div>`;
  return html`<${PageCard} className="retention-page" header=${html`<${PageHeader} icon="IconHeartHandshake" title=${t('retention.title')} actions=${actions} />`}>
    <div class="retention-page-body" data-testid="retention-page">
      <div class="retention-page-intro">
        <p class="retention-muted">${t('retention.subtitle')}</p>
        ${ready ? html`<p class="retention-threshold">${t('retention.page.threshold', { threshold: ready.threshold })}</p>` : null}
      </div>
      <${TabList} tabs=${tabs} activeTabId=${tab} onChange=${(id) => updateSearchParams({ tab: id === 'cases' ? null : id })} />
      <div class="retention-tab-panel" role="tabpanel" aria-labelledby=${`tab-${tab}`}>
        ${overview === null
          ? html`<div class="retention-list">${[0, 1, 2].map((index) => html`<${Skeleton} key=${index} width="100%" height=${72} />`)}</div>`
          : overview === 'error'
            ? html`<div class="retention-empty"><${EmptyState} icon="IconAlertTriangle" title=${t('retention.page.loadError')} /></div>`
            : tab === 'cases' ? html`<${CasesTab} overview=${overview} onChanged=${load} />`
              : tab === 'households' ? html`<${HouseholdsTab} overview=${overview} onChanged=${load} />`
                : html`<${SignalsTab} overview=${overview} />`}
      </div>
    </div>
    <${RetentionDialogHost} />
  <//>`;
}
